import { z } from 'zod';
/**
 * The media asset manager's vocabulary, shared by the server and the page. Every asset the newsroom can find is
 * named by a key `<kind>:<id>`: a clip of the playout's library, a file imported into a story, a music bed of the
 * station, a voice-over take of a story. The newsroom's own knowledge of an asset (a description, keywords, named
 * ranges called subclips) and each user's basket refer to assets by that key.
 *
 * The model follows the asset browser of Nebula (github.com/nebulabroadcast/nebula, the open-source broadcast MAM):
 * one catalogue seen through views, a preview with in and out marks stepped by frames, subclips, a VU meter, and
 * a basket of ranges sent on together (here: into a new edit).
 */
export const MAM_KINDS = ['clip', 'upload', 'music', 'voiceover'];
export const MAM_MEDIA = ['video', 'audio'];
/** The views of the catalogue the page's navigation offers. */
export const MAM_SECTIONS = ['all', 'clip', 'upload', 'music', 'voiceover', 'used', 'unused', 'new'];
export const MAM_SORTS = ['inserted', 'title', 'duration', 'kind', 'size', 'status', 'file'];
export const MAM_PAGE_SIZES = [25, 50, 100, 200];
/** An asset first seen this many hours ago or less is "new". */
export const MAM_NEW_HOURS = 24;
export const MAX_TAGS = 30;
export const MAX_SUBCLIPS = 100;
export const MAX_BASKET = 200;
/** The frame rate the player steps and the lists count in (the station's PAL rate). */
export const MAM_FPS = 25;
export const mediaOfKind = (k) => (k === 'clip' || k === 'upload' ? 'video' : 'audio');
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** An asset key read back into its kind and id; null for anything else (the playout's ids are free text, the rest are UUIDs). */
export function parseAssetKey(key) {
    const i = key.indexOf(':');
    if (i <= 0)
        return null;
    const kind = key.slice(0, i);
    const id = key.slice(i + 1);
    if (!MAM_KINDS.includes(kind) || !id || id.length > 200)
        return null;
    if (kind !== 'clip' && !UUID.test(id))
        return null;
    return { kind, id };
}
export const assetKeyOf = (r) => `${r.kind}:${r.id}`;
export const mamSubclipSchema = z
    .object({
    id: z.string().min(1).max(40),
    name: z.string().trim().max(120),
    inMs: z.number().int().min(0),
    outMs: z.number().int().min(0),
})
    .refine((s) => s.outMs > s.inMs, { message: 'outMs > inMs' });
export const mamMetaSchema = z.object({
    description: z.string().max(4000),
    tags: z.array(z.string().max(60)).max(MAX_TAGS * 2),
    subclips: z.array(mamSubclipSchema).max(MAX_SUBCLIPS),
});
/** Keywords as stored: trimmed, without empties or repeats (letter case and accents ignored, the first spelling kept), at most MAX_TAGS. */
export function normaliseTags(tags) {
    const seen = new Set();
    const out = [];
    for (const raw of tags) {
        const tag = raw.replace(/\s+/g, ' ').trim().slice(0, 60);
        const k = tag.toLocaleLowerCase('el').normalize('NFD').replace(/\p{M}/gu, '');
        if (!tag || seen.has(k))
            continue;
        seen.add(k);
        out.push(tag);
        if (out.length >= MAX_TAGS)
            break;
    }
    return out;
}
/** A range inside an asset of a known length: the marks kept inside it; null when nothing of it is left. */
export function clampRange(inMs, outMs, durationMs) {
    const end = durationMs ?? Number.POSITIVE_INFINITY;
    const a = inMs === null ? null : Math.max(0, Math.min(Math.round(inMs), end));
    const b = outMs === null ? null : Math.max(0, Math.min(Math.round(outMs), end));
    if (a !== null && b !== null && b <= a)
        return null;
    if (a !== null && Number.isFinite(end) && a >= end)
        return null;
    return { inMs: a, outMs: b };
}
/** The length a range plays: from the in mark (or the start) to the out mark (or the end); null when the end is unknown. */
export function rangeLengthMs(inMs, outMs, durationMs) {
    const end = outMs ?? durationMs;
    if (end === null)
        return null;
    return Math.max(0, end - (inMs ?? 0));
}
/** Subclips as stored: names trimmed (an empty one named after its marks), ranges kept inside the asset, in order of their in marks. */
export function normaliseSubclips(subclips, durationMs) {
    const out = [];
    for (const s of subclips) {
        const r = clampRange(s.inMs, s.outMs, durationMs);
        if (!r || r.inMs === null || r.outMs === null)
            continue;
        out.push({ id: s.id, name: s.name.trim() || `${frameTc(r.inMs)} – ${frameTc(r.outMs)}`, inMs: r.inMs, outMs: r.outMs });
    }
    return out.sort((a, b) => a.inMs - b.inMs || a.outMs - b.outMs).slice(0, MAX_SUBCLIPS);
}
const pad = (n, w = 2) => String(n).padStart(w, '0');
/** A time as broadcast timecode `HH:MM:SS:FF` (frames at `fps`, the frame the time falls in). */
export function frameTc(ms, fps = MAM_FPS) {
    const frames = Math.max(0, Math.floor((Math.max(0, ms) * fps) / 1000 + 1e-6));
    const f = frames % fps;
    const s = Math.floor(frames / fps);
    return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}:${pad(f)}`;
}
/**
 * Timecode typed by a person into milliseconds: `HH:MM:SS:FF`, `HH:MM:SS`, `MM:SS`, `SS` or seconds with a
 * decimal point (`12.5`); null when it is none of these or a field is out of range.
 */
export function parseFrameTc(text, fps = MAM_FPS) {
    const t = text.trim().replace(',', '.');
    if (/^\d+(\.\d+)?$/.test(t))
        return Math.round(Number(t) * 1000);
    const parts = t.split(':');
    if (parts.length < 2 || parts.length > 4 || parts.some((p) => !/^\d{1,3}$/.test(p)))
        return null;
    const n = parts.map(Number);
    let h = 0;
    let m = 0;
    let s = 0;
    let f = 0;
    if (n.length === 4)
        [h, m, s, f] = n;
    else if (n.length === 3)
        [h, m, s] = n;
    else
        [m, s] = n;
    if ((n.length > 2 && m > 59) || s > 59 || f >= fps)
        return null;
    return Math.round((h * 3600 + m * 60 + s) * 1000 + (f * 1000) / fps);
}
/** A time moved by whole frames, landing on the start of a frame (never before zero, never past the end). */
export function stepFrames(ms, frames, durationMs, fps = MAM_FPS) {
    const frame = Math.floor((Math.max(0, ms) * fps) / 1000 + 1e-6) + frames;
    const out = Math.max(0, (frame * 1000) / fps);
    return durationMs === null ? out : Math.min(out, Math.max(0, durationMs - 1000 / fps));
}
export const mamBasketAddSchema = z.object({
    items: z
        .array(z.object({
        assetKey: z.string().min(3).max(220),
        inMs: z.number().int().min(0).nullable().optional(),
        outMs: z.number().int().min(0).nullable().optional(),
        label: z.string().trim().max(120).optional(),
    }))
        .min(1)
        .max(MAX_BASKET),
});
//# sourceMappingURL=mam.js.map